import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, apiPatch } from "@/lib/api";

export type SitePhotoParentType =
  | "project"
  | "daily_log"
  | "punch_list_item"
  | "inspection_checklist"
  | "safety_incident";

/** Mirrors `SitePhotoDto` from the API. */
export interface SitePhoto {
  id: string;
  projectId: string;
  orgId: string;
  fileId: string;
  capturedAt: string;
  takenByUserId: string | null;
  takenByName: string | null;
  category: string;
  location: string | null;
  latitude: number | null;
  longitude: number | null;
  caption: string | null;
  tags: string[] | null;
  parentType: SitePhotoParentType | null;
  parentId: string | null;
  dailyLogId: string | null;
  punchListItemId: string | null;
  inspectionChecklistId: string | null;
  safetyIncidentId: string | null;
  fullUrl: string;
  thumbnailUrl: string;
  createdAt: string;
  updatedAt: string;
}

const sitePhotoKeys = {
  all: ["site-photos"] as const,
  project: (id: string) => ["site-photos", "project", id] as const,
  filtered: (filters?: Record<string, unknown>) =>
    ["site-photos", "filtered", filters ?? null] as const,
} as const;

export { sitePhotoKeys };

interface SitePhotoFilters {
  projectId?: string;
  dailyLogId?: string;
  punchListItemId?: string;
  category?: string;
}

function buildSitePhotoQuery(filters?: SitePhotoFilters): string {
  const params = new URLSearchParams();
  if (filters?.projectId) params.set("projectId", filters.projectId);
  if (filters?.dailyLogId) params.set("dailyLogId", filters.dailyLogId);
  if (filters?.punchListItemId) params.set("punchListItemId", filters.punchListItemId);
  if (filters?.category) params.set("category", filters.category);
  const qs = params.toString();
  return qs ? `/site-photos?${qs}` : "/site-photos";
}

export function useSitePhotos(filters?: SitePhotoFilters) {
  return useQuery({
    queryKey: sitePhotoKeys.filtered(filters as Record<string, unknown> | undefined),
    queryFn: () => apiFetch<SitePhoto[]>(buildSitePhotoQuery(filters)),
    enabled: Boolean(filters?.projectId?.length),
  });
}

interface UploadSitePhotoInput {
  file: File;
  projectId: string;
  lat?: number;
  lng?: number;
  dailyLogId?: string;
  punchListItemId?: string;
  caption?: string;
  category?: string;
  location?: string;
  capturedAt?: string;
  tags?: string[];
}

async function uploadSitePhotoFn(input: UploadSitePhotoInput): Promise<SitePhoto> {
  const form = new FormData();
  form.append("file", input.file);
  form.append("projectId", input.projectId);
  if (input.lat !== undefined) form.append("lat", String(input.lat));
  if (input.lng !== undefined) form.append("lng", String(input.lng));
  if (input.dailyLogId) form.append("dailyLogId", input.dailyLogId);
  if (input.punchListItemId) form.append("punchListItemId", input.punchListItemId);
  if (input.caption) form.append("caption", input.caption);
  if (input.category) form.append("category", input.category);
  if (input.location) form.append("location", input.location);
  if (input.capturedAt) form.append("capturedAt", input.capturedAt);
  if (input.tags?.length) form.append("tags", input.tags.join(","));

  const res = await fetch("/api/site-photos", { method: "POST", body: form });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `API error: ${res.status}` }));
    throw new Error((err as { error?: string }).error ?? `API error: ${res.status}`);
  }
  return res.json() as Promise<SitePhoto>;
}

export function useUploadSitePhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: uploadSitePhotoFn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sitePhotoKeys.all });
      toast.success("Photo uploaded");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to upload photo");
    },
  });
}

interface UpdateSitePhotoInput {
  id: string;
  caption?: string | null;
  category?: string | null;
  location?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  tags?: string[] | null;
  dailyLogId?: string | null;
}

export function useUpdateSitePhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateSitePhotoInput) =>
      apiPatch<SitePhoto>(`/site-photos/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sitePhotoKeys.all });
      toast.success("Photo updated");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to update photo");
    },
  });
}
